import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Droplets, Loader2, Phone } from "lucide-react";
import api from "@/lib/api";

// Jun 2026 — office number for stranded techs ONLY. Deliberately local to
// this screen: the site-wide CONTACT number is unchanged.
const OFFICE_PHONE = "07 5238 8182";
const OFFICE_PHONE_LINK = "tel:+61752388182";

// Aug 2026 — technician access is a 4-digit PIN (replaces email magic
// link as the primary flow). ServiceM8 deep link → PIN → inspection.
export default function FieldLogin() {
  const [params] = useSearchParams();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const submit = async (pin) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/field/auth/pin", { pin, next: params.get("next") || "/field/today" });
      localStorage.setItem("scwt_token", data.token);
      window.location.assign(data.next || "/field/today");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Incorrect PIN.");
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
      setBusy(false);
    }
  };

  const onDigit = (i, v) => {
    const c = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = c;
    setDigits(next);
    if (c && i < 3) refs[i + 1].current?.focus();
    if (c && i === 3 && next.every((d) => d !== "")) submit(next.join(""));
  };
  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };
  const onPaste = (e) => {
    const p = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (p.length === 4) { e.preventDefault(); setDigits(p.split("")); submit(p); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 max-w-md mx-auto" data-testid="field-login-page">
      <div className="flex items-center gap-2 justify-center">
        <Droplets className="w-7 h-7 text-cyan-600" />
        <div className="text-lg font-bold text-slate-900">AWARE Field Access</div>
      </div>
      <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-6 text-center">
        <h1 className="text-base font-bold text-slate-900">Enter your 4-digit PIN</h1>
        <p className="mt-1 text-xs text-slate-500">Your PIN identifies you — the inspection opens straight after.</p>
        <div className="mt-5 flex justify-center gap-3" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input key={i} ref={refs[i]} value={d} disabled={busy}
              onChange={(e) => onDigit(i, e.target.value)} onKeyDown={(e) => onKey(i, e)}
              type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
              autoFocus={i === 0} maxLength={2}
              data-testid={`field-pin-digit-${i}`}
              className="w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 border-slate-300 focus:border-cyan-500 focus:outline-none disabled:opacity-50" />
          ))}
        </div>
        {busy && (
          <div className="mt-4 flex items-center justify-center gap-2 text-slate-500 text-sm" data-testid="field-pin-busy">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-600" /> Signing you in…
          </div>
        )}
        {err && (
          <div className="mt-4" data-testid="field-pin-error">
            <p className="text-sm font-semibold text-rose-600">{err}</p>
            {/* Jun 2026 — a tech stuck on this screen onsite has no way
                forward unless we tell them who to ring. */}
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-900">Never been given a PIN?</p>
              <p className="mt-1 text-xs text-amber-800">
                Your PIN is set by the AWARE office — it isn&apos;t your ServiceM8 login.
                Call the lab and they can set it while you wait.
              </p>
              <a href={OFFICE_PHONE_LINK} data-testid="field-pin-call-office"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-amber-900 underline">
                <Phone className="w-3.5 h-3.5" /> {OFFICE_PHONE}
              </a>
            </div>
          </div>
        )}
        {!err && <p className="mt-6 text-[11px] text-slate-400">No PIN yet? Call the AWARE office on {OFFICE_PHONE} and they&apos;ll set one.</p>}
      </div>
    </div>
  );
}

// Kept for any old magic-link emails still in inboxes.
export function FieldVerify() {
  const [params] = useSearchParams();
  const [err, setErr] = useState("");
  useEffect(() => {
    const token = params.get("token");
    if (!token) { setErr("This sign-in link is invalid or has expired."); return; }
    api.post("/field/auth/verify", { token })
      .then(({ data }) => {
        localStorage.setItem("scwt_token", data.token);
        window.history.replaceState({}, "", "/field/verify");
        window.location.assign(data.next || "/field/today");
      })
      .catch((e) => setErr(e.response?.data?.detail || "This sign-in link is invalid or has expired."));
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="field-verify-page">
      {err ? (
        <div className="text-center">
          <p className="text-sm font-semibold text-slate-900" data-testid="field-verify-error">{err}</p>
          <a href="/field/login" className="mt-2 inline-block text-xs font-bold text-cyan-700 underline">Sign in with your PIN</a>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /> Signing you in…</div>
      )}
    </div>
  );
}
